import { Navigate, useParams } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resolveEntityUrl } from '@/lib/entityNavigation';

/**
 * `/go/:entityType/:entityId` — an entity-agnostic deep link.
 *
 * Server-side producers (notification emails, integrations) need a stable URL for "this
 * record", but the entity→route map lives in the generated navigation config, which is
 * frontend knowledge. Mirroring it on the server would mean two maps drifting apart; this
 * route lets the server emit one shape for every entity and lets the client resolve it.
 *
 * An unknown entity type says so rather than redirecting somewhere plausible — a mail from
 * six months ago naming an entity that has since been renamed should not silently land the
 * reader on the wrong record.
 */
export function EntityRedirect() {
  const { t } = useTranslation();
  const { entityType, entityId } = useParams<{ entityType: string; entityId: string }>();

  const target = entityId ? resolveEntityUrl(entityType, entityId) : null;

  if (target) {
    return <Navigate to={target} replace />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="warning">
        {t('Common.EntityRedirect.Unknown', {
          defaultValue: 'This link points at a record type this app does not know: {{entityType}}.',
          entityType: entityType ?? '—',
        })}
      </Alert>
    </Box>
  );
}
